import {
  Component,
  NgZone,
  OnDestroy,
  OnInit,
  Optional,
  QueryList,
  TemplateRef,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { GoogleMapsModule } from '@angular/google-maps';
import { LocationPickerComponent } from '../../../../common/components/location-picker/location-picker.component';
import { lastValueFrom } from 'rxjs/internal/lastValueFrom';
import { PolicyStepComponent } from '../../../claim/ui/components/policy-step/policy-step.component';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { CraneService } from '../../services/crane.service';
import Policy from '../../../../core/data/models/policy.mode';
import { SwalService } from '../../../../common/services/swal.service';
import { AuthService } from '../../../auth/services/auth.service';
import { CraneRequest } from '../../../../core/data/models/crane-request.model';
import moment from 'moment';
import { SharedModule } from '../../../../common/shared.module';
import { policyMask } from '../../../../common/constants/masks';
import { ClientSelectorComponent } from '../../../../common/components/client-selector/client-selector.component';
import ProducerClientDto from '../../../../core/data/dtos/producer-client.dto';
import { ClientButtonComponent } from '../../../../common/components/client-button/client-button.component';
import { Subscription } from 'rxjs';
import PolicyTypes from '../../../../common/enums/policyTypes';
import { Router } from '@angular/router';
import { NgbModal, NgbProgressbarModule } from '@ng-bootstrap/ng-bootstrap';
import { Location } from '@angular/common';

@Component({
  selector: 'app-crane-list',
  imports: [
    ReactiveFormsModule,
    GoogleMapsModule,
    PolicyStepComponent,
    SharedModule,
    ClientSelectorComponent,
    ClientButtonComponent,
    LocationPickerComponent,
    NgbProgressbarModule,
  ],
  providers: [CraneService],
  templateUrl: './crane-list.component.html',
  styleUrl: './crane-list.component.css',
})
export class CraneListComponent implements OnInit, OnDestroy {
  @ViewChild('successModal') successModal!: TemplateRef<any>;
  @ViewChildren(LocationPickerComponent) locationPickers!: QueryList<LocationPickerComponent>;

  center: google.maps.LatLngLiteral = { lat: 18.4626, lng: -69.9361 };
  zoom = 10;
  userSubscription?: Subscription;
  activeStep = 1;

  steps: { [key: string]: number } = {
    policy: 1,
    contact: 2,
    crane: 3,
  };

  client?: ProducerClientDto;
  policySelected: Policy | null = null;

  craneForm = this.fb.group({
    policyNumber: ['', Validators.required],
    phoneNumber: ['', Validators.required],
    address: ['', Validators.required],
    closeTo: ['', Validators.required],
    description: ['', Validators.required],
    email: ['', [Validators.required, Validators.email]],
    latitude: [18.4800379, Validators.required],
    longitude: [-69.9880794, Validators.required],
    wDate: [''],
    isMailSent: [true, Validators.required],
    idNumber: ['', Validators.required],
    clientName: ['', Validators.required],
    province: ['', Validators.required],
    municipality: ['', Validators.required],
  });

  policyMask = policyMask;
  namePattern = new RegExp('[a-zA-ZáéíóúÁÉÍÓÚñÑ ]');
  protected readonly PolicyTypes = PolicyTypes;

  constructor(
    private fb: FormBuilder,
    private craneService: CraneService,
    private swalService: SwalService,
    private router: Router,
    private ngZone: NgZone,
    private location: Location,
    private modalService: NgbModal,
    @Optional() private authService?: AuthService,
  ) {
    if (this.hasUser) {
      this.craneForm.patchValue({
        idNumber: this.authService!.user.idTokenClaims?.extension_IdNumber,
      });
    }
  }

  get hasUser() { return !!this.authService?.user; }
  get isIntermediary() { return this.authService?.isIntermediary; }
  get isClient() { return this.authService?.isClient; }

  get totalSteps(): number {
    return Math.max(...Object.values(this.steps));
  }

  get stepsArray(): number[] {
    return Array.from({ length: this.totalSteps }, (_, i) => i + 1);
  }

  get progressValue(): number {
    return (this.activeStep / this.totalSteps) * 100;
  }

  get stepTitle(): string {
    const { client: c, policy: p, contact: co, crane: cr } = this.steps;
    if (c && this.activeStep === c) return 'Elige el cliente para tu solicitud';
    if (this.activeStep === p) return 'Elige la póliza para tu solicitud';
    if (this.activeStep === co) return 'Información de contacto';
    if (this.activeStep === cr) return '¿Dónde necesitas la Asistencia Vial?';
    return '';
  }

  get isCurrentStepValid(): boolean {
    const c = this.craneForm.controls;
    const { client: clientStep, policy: policyStep, contact: contactStep } = this.steps;

    if (clientStep && this.activeStep === clientStep) return !!this.client;
    if (this.activeStep === policyStep) {
      if (this.hasUser) return !!this.policySelected;
      return c.policyNumber.valid && c.idNumber.valid;
    }
    if (this.activeStep === contactStep) {
      return c.clientName.valid && c.email.valid && c.phoneNumber.valid;
    }
    return true;
  }

  get showPolicySelector(): boolean {
    return this.hasUser && (!!this.isClient || (!!this.isIntermediary && !!this.client));
  }

  ngOnInit() {
    this.userSubscription = this.authService?.userSubject.subscribe((user) => {
      if (user.phone) {
        this.craneForm.patchValue({ phoneNumber: user.phone });
      }
    });

    if (this.hasUser) {
      this.steps = { policy: 1, contact: 2, crane: 3 };
      const emails = this.authService?.user.idTokenClaims?.emails;
      this.craneForm.patchValue({
        clientName: this.authService?.user.idTokenClaims?.given_name,
        email: emails ? emails[0] : '',
      });
    }

    if (this.isIntermediary) {
      this.steps = { client: 1, policy: 2, contact: 3, crane: 4 };
    }
  }

  ngOnDestroy() {
    this.userSubscription?.unsubscribe();
  }

  nextStep() {
    if (this.activeStep < this.totalSteps) {
      this.activeStep += 1;
    }
  }

  onNextPressed() {
    if (!this.isCurrentStepValid) {
      this.markCurrentStepControls();
      this.showMissingFieldsAlert();
      return;
    }
    this.nextStep();
  }

  onSubmitPressed() {
    if (this.craneForm.invalid) {
      this.markCurrentStepControls();
      this.showMissingFieldsAlert();
      return;
    }
    this.onSubmit();
  }

  private markCurrentStepControls() {
    const c = this.craneForm.controls;
    const step = this.activeStep;
    const { policy: ps, contact: cos, crane: crs } = this.steps;

    const touch = (ctrl: { markAsDirty(): void; updateValueAndValidity(): void }) => {
      ctrl.markAsDirty();
      ctrl.updateValueAndValidity();
    };

    if (step === ps && !this.hasUser) {
      touch(c.policyNumber);
      touch(c.idNumber);
    } else if (step === cos) {
      touch(c.clientName);
      touch(c.email);
      touch(c.phoneNumber);
    } else if (step === crs) {
      touch(c.address);
      touch(c.province);
      touch(c.municipality);
      touch(c.closeTo);
      touch(c.description);
      this.locationPickers?.find(p => p.mapId === 'assistance-location-map')
        ?.markAsTouchedIfInvalid(c.address.invalid);
    }
  }

  private getMissingFields(): string[] {
    const c = this.craneForm.controls;
    const step = this.activeStep;
    const { client: cs, policy: ps, contact: cos, crane: crs } = this.steps;
    const missing: string[] = [];

    if (cs && step === cs) {
      if (!this.client) missing.push('Cliente');
    } else if (step === ps) {
      if (!this.hasUser) {
        if (c.policyNumber.invalid) missing.push('No. de póliza');
        if (c.idNumber.invalid) missing.push('No. de cédula o pasaporte');
      }
    } else if (step === cos) {
      if (c.clientName.invalid) missing.push('Nombre completo');
      if (c.email.invalid) missing.push('Correo electrónico');
      if (c.phoneNumber.invalid) missing.push('Teléfono');
    } else if (step === crs) {
      if (c.address.invalid) missing.push('Dirección de asistencia');
      if (c.province.invalid) missing.push('Provincia');
      if (c.municipality.invalid) missing.push('Municipio');
      if (c.closeTo.invalid) missing.push('Lugar o establecimiento cercano');
      if (c.description.invalid) missing.push('Descripción adicional');
    }

    return missing;
  }

  private showMissingFieldsAlert() {
    const missing = this.getMissingFields();
    if (!missing.length) return;
    this.swalService.fieldsRequired(missing).then(() => this.focusFirstInvalidField());
  }

  private focusFirstInvalidField() {
    const c = this.craneForm.controls;
    const step = this.activeStep;
    const { policy: ps, contact: cos, crane: crs } = this.steps;

    const tryFocus = (selector: string) => {
      (document.querySelector(selector) as HTMLElement)?.focus();
    };

    if (step === ps) {
      if (c.policyNumber.invalid) { tryFocus('[formControlName="policyNumber"]'); return; }
      if (c.idNumber.invalid) { tryFocus('[formControlName="idNumber"]'); return; }
    } else if (step === cos) {
      if (c.clientName.invalid) { tryFocus('[formControlName="clientName"]'); return; }
      if (c.email.invalid) { tryFocus('[formControlName="email"]'); return; }
      if (c.phoneNumber.invalid) { tryFocus('[formControlName="phoneNumber"]'); return; }
    } else if (step === crs) {
      if (c.address.invalid) {
        // location picker: find by mapId attribute
        const picker = document.querySelector('app-location-picker[ng-reflect-map-id="assistance-location-map"] input');
        (picker as HTMLElement)?.focus();
        return;
      }
      if (c.province.invalid) { tryFocus('[formControlName="province"]'); return; }
      if (c.municipality.invalid) { tryFocus('[formControlName="municipality"]'); return; }
      if (c.closeTo.invalid) { tryFocus('[formControlName="closeTo"]'); return; }
      if (c.description.invalid) { tryFocus('[formControlName="description"]'); }
    }
  }

  prevStep() {
    if (this.activeStep > 1) {
      this.activeStep -= 1;
    } else {
      this.location.back();
    }
  }

  async onSubmit() {
    this.craneForm.patchValue({ wDate: moment().format('YYYY-MM-DD') });

    if (this.craneForm.invalid) return;

    try {
      this.swalService.showLoading('Solicitando asistencia');

      const crane = this.craneForm.value as CraneRequest;
      const result = await lastValueFrom(
        this.craneService.create(crane, '', undefined, false),
      );

      if (result.succeeded) {
        this.swalService.dismiss();
        this.craneForm.reset();
        const modalRef = this.modalService.open(this.successModal, { size: 'lg', centered: true, backdrop: 'static' });
        modalRef.result.finally(() => this.router.navigate(['/dashboard']));
      } else {
        this.swalService.error('No se pudo enviar la solicitud.');
      }
    } catch (e) {
      this.swalService.error('No se pudo enviar la solicitud.');
    }
  }

  onCranePlaceSelected($event: { fullAddress?: string; street?: string; location: google.maps.LatLngLiteral; components?: google.maps.GeocoderAddressComponent[] }) {
    if ($event.location) {
      this.center = $event.location;
      this.zoom = 17;
      const comps = $event.components || [];
      const province = comps.find((ac) => ac.types.includes('administrative_area_level_1'))?.long_name;
      const municipality = comps.find((ac) => ac.types.includes('administrative_area_level_2'))?.long_name;
      if (!province || !municipality) {
        this.swalService.error('No se pudo encontrar la ubicación');
        return;
      }
      this.craneForm.patchValue({
        address: $event.street || $event.fullAddress || '',
        province,
        municipality,
        latitude: this.center.lat as any,
        longitude: this.center.lng as any,
      });
    }
  }

  onCraneDragEnd($event: { fullAddress?: string; street?: string; location: google.maps.LatLngLiteral; components?: google.maps.GeocoderAddressComponent[] }) {
    const latitude = $event.location.lat;
    const longitude = $event.location.lng;
    if (latitude && longitude) {
      this.center = $event.location;
      const patch: any = { latitude, longitude };
      const addressValue = $event.street || $event.fullAddress;
      if (addressValue) patch.address = addressValue;
      const comps = $event.components || [];
      const province = comps.find((ac) => ac.types.includes('administrative_area_level_1'))?.long_name;
      const municipality = comps.find((ac) => ac.types.includes('administrative_area_level_2'))?.long_name;
      if (province) patch.province = province;
      if (municipality) patch.municipality = municipality;
      this.craneForm.patchValue(patch);
    }
  }

  onCraneAddressChanged(address?: string) {
    this.craneForm.patchValue({ address: address || '' });
  }

  onPolicySelected(policy: Policy) {
    this.policySelected = policy;
    this.craneForm.patchValue({ policyNumber: policy.policyNumber });
    this.nextStep();
  }

  handleClientSelected($event: ProducerClientDto) {
    this.client = $event;
    this.nextStep();
  }
}
